import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { createEsteira, updateEsteira, deleteEsteira } from '../../../api/esteirasApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { Esteira, EsteiraRole } from '../../../interfaces/Esteira';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { ESTEIRA } from '../../../shared/esteiras';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). O import é
// explícito de propósito: contar com o CSS que a página hospedeira carrega é acoplamento
// invisível — a aba quebraria em silêncio se fosse montada em outra tela (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';

/**
 * Aba "Fluxos de Esteiras" da Administração do Sistema — CRUD das etapas dinâmicas.
 *
 * Extraída de `AdministracaoSistemaPage.tsx` na at-73 (ver o comentário irmão em
 * `EsteiraTagsSection.tsx`). Comportamento idêntico ao anterior: a lista vem do
 * `useEsteiras` e as mutações invalidam a query `['esteiras']`, que é o que atualiza
 * todas as telas que dependem de esteira.
 */

// ADMIN não aparece: o backend sempre autoriza ADMIN (esteiras e tags), marcar seria redundante.
const ALL_ROLES = [
  'RECEPCAO', 'CHEFE_DE_OFICINA', 'COLABORADOR', 'ORCAMENTO',
  'COMPRA', 'ESTOQUE',
];

// Pré-configuração de esteira nova (espelho de ESTEIRA_ROLES_PADRAO no backend):
// solicitar — Chefe e Colaborador; aprovar — Chefe; trabalhar — ninguém. ADMIN é implícito.
const ESTEIRA_ROLES_PADRAO: EsteiraRole[] = [
  { role: 'CHEFE_DE_OFICINA', tipo: 'SOLICITAR_APROVACAO' },
  { role: 'COLABORADOR', tipo: 'SOLICITAR_APROVACAO' },
  { role: 'CHEFE_DE_OFICINA', tipo: 'APROVAR' },
];

/**
 * As 5 esteiras do ciclo de vida têm a marcação de sistema IMUTÁVEL (o backend recusa com 403).
 *
 * ⚠️ A comparação é pela CHAVE, nunca por `est.sistema`: desde a at-84 (ADR 0038) "é de sistema"
 * deixou de significar "é do ciclo de vida" — o ADMIN marca esteiras de trabalho como sistema.
 */
const CHAVES_CICLO_DE_VIDA: string[] = Object.values(ESTEIRA);

const HINT_SISTEMA =
  'Esteira de sistema fica sempre disponível ao alterar a esteira de um veículo, mesmo fora do ' +
  'planejamento dele, e não passa pela liberação administrativa. Em troca, não entra no ' +
  'planejamento de serviços e não tem acompanhamento de serviço.';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que são as esteiras',
    conteudo: (
      <p>
        Esteiras são as etapas por onde os veículos passam na oficina (Funilaria, Pintura, Mecânica...). Elas são{' '}
        <strong>dinâmicas</strong>: é aqui que se cria, edita, ordena e desativa cada etapa — a mudança vale para
        o sistema inteiro (Painel Geral, planejamento, indicadores).
      </p>
    ),
  },
  {
    titulo: 'Esteiras de sistema',
    conteudo: (
      <>
        <p>
          As etapas marcadas com o selo <strong>SISTEMA</strong> são as de <strong>fluxo livre</strong>: ficam
          sempre disponíveis ao <strong>alterar a esteira</strong> de um veículo, mesmo que não estejam no
          planejamento dele; não dependem de <strong>liberação administrativa</strong> para receber o veículo; e
          não restringem colaborador por <strong>especialidade</strong>. Em troca, não entram no planejamento de
          serviços, não têm acompanhamento de serviço (previsto, inacabado, concluído) e não podem ser
          desativadas nem excluídas enquanto tiverem a marcação.
        </p>
        <p>
          Cinco delas são <strong>fixas</strong> — espera, definição da próxima etapa, orçamento, aguardo de
          entrega e entrega. Fazem parte do ciclo de vida do veículo, e a marcação delas não pode ser alterada.
          As demais podem ser marcadas ou desmarcadas aqui, e servem para etapas em que o veículo é colocado{' '}
          <strong>manualmente</strong>, sem passar por planejamento — por exemplo, por decisão de negócio
          (26/08/2026), <strong>Sindicância</strong> e <strong>Indenização Integral</strong>.
        </p>
        <p>
          Atenção: marcar uma esteira como sistema <strong>dispensa a liberação administrativa</strong> dela — o
          veículo passa a poder ser movido para lá sem autorização da Administração. A marcação só pode ser
          alterada em esteira <strong>sem veículos ativos</strong>, e a esteira precisa estar fora das
          especialidades.
        </p>
        <p>
          Se já houver veículos <strong>liberados</strong> para essa esteira, o sistema mostra a lista antes de
          aplicar e pede confirmação: como a etapa passa a ser de fluxo livre, essas liberações deixam de ter
          efeito e são <strong>removidas</strong>. Os veículos continuam entrando na esteira normalmente — o que
          some é a autorização, que ali deixou de ser exigida.
        </p>
      </>
    ),
  },
  {
    titulo: 'Cor, ordem, SLA e colaborador',
    conteudo: (
      <>
        <p>
          <strong>Cor Hex</strong> é a cor da esteira em todo o sistema. <strong>Ordem</strong> define a posição
          nas listas e no quadro. <strong>SLA (Dias)</strong> é o prazo padrão de permanência — alimenta os
          alertas de prazo e o valor sugerido ao aprovar uma mudança de esteira; vazio significa sem limite.
        </p>
        <p>
          <strong>Planejável</strong> permite incluir a esteira no planejamento dos veículos — esteira de
          sistema nunca é planejável, então as duas marcações não convivem.{' '}
          <strong>Permite colaborador</strong> autoriza vincular colaboradores e registrar horas nela —
          desmarcada, o veículo passa pela etapa sem apontamento de trabalho.{' '}
          <strong>Conta no total na oficina</strong> decide se os veículos da esteira entram no card
          <strong> Total na oficina</strong> do Painel Geral — desmarcada (como em Indenização Integral e
          Negativa), o veículo continua no quadro, só sai da contagem.
        </p>
      </>
    ),
  },
  {
    titulo: 'Permissões de Perfil',
    conteudo: (
      <p>
        A matriz define, por perfil, quem pode <strong>Solicitar Entrada</strong> (pedir que um veículo vá para
        esta esteira — a solicitação fica pendente) e quem pode <strong>Aprovar Entrada</strong> (efetivar a
        mudança). O administrador sempre pode as duas coisas. Colaborador que solicita nunca move o veículo
        sozinho — sempre há uma aprovação.
      </p>
    ),
  },
  {
    titulo: 'Desativar e excluir',
    conteudo: (
      <p>
        <strong>Desativar</strong> tira a esteira das opções novas sem afetar o histórico dos veículos que
        passaram por ela. <strong>Excluir</strong> remove a etapa de vez — e falha se houver veículos ativos nela
        (o sistema avisa). Esteiras de sistema não têm o botão Excluir.
      </p>
    ),
  },
];

interface FluxoEsteirasSectionProps {
  /** Informa o hospedeiro se esta aba ainda está carregando (usa o loader da página). */
  onLoadingChange?: (loading: boolean) => void;
}

const FluxoEsteirasSection: React.FC<FluxoEsteirasSectionProps> = ({ onLoadingChange }) => {
  const queryClient = useQueryClient();
  const { esteiras, isLoading: loadingEsteiras } = useEsteiras(true);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // Nova esteira
  const [novoEstNome, setNovoEstNome] = useState('');
  const [novoEstCor, setNovoEstCor] = useState('#ea5d0e');
  const [novoEstOrdem, setNovoEstOrdem] = useState('1');
  const [novoEstSla, setNovoEstSla] = useState('');
  const [novoEstPlanejavel, setNovoEstPlanejavel] = useState(true);
  const [novoEstPermiteColaborador, setNovoEstPermiteColaborador] = useState(false);
  const [novoEstSistema, setNovoEstSistema] = useState(false);
  const [novoEstRoles, setNovoEstRoles] = useState<EsteiraRole[]>([...ESTEIRA_ROLES_PADRAO]);
  const [criandoEsteira, setCriandoEsteira] = useState(false);

  // Edição
  const [editEstId, setEditEstId] = useState<string | null>(null);
  const [editEstCor, setEditEstCor] = useState('');
  const [editEstOrdem, setEditEstOrdem] = useState('');
  const [editEstSla, setEditEstSla] = useState('');
  const [editEstPlanejavel, setEditEstPlanejavel] = useState(true);
  const [editEstPermiteColaborador, setEditEstPermiteColaborador] = useState(false);
  const [editEstContaNoTotal, setEditEstContaNoTotal] = useState(true);
  const [editEstSistema, setEditEstSistema] = useState(false);
  const [editEstRoles, setEditEstRoles] = useState<EsteiraRole[]>([]);
  const [salvandoEsteira, setSalvandoEsteira] = useState(false);
  const [esteiraParaExcluir, setEsteiraParaExcluir] = useState<Esteira | null>(null);
  /**
   * BUG-006: marcar uma esteira como de sistema torna-a de fluxo livre, e as liberações
   * administrativas concedidas para ela deixam de valer. O backend recusa a primeira tentativa
   * (409 ESTEIRA_TEM_LIBERACOES) sem gravar nada, listando os veículos — quem marca precisa ver
   * o que está desfazendo antes de desfazer. Molde do impacto das especialidades (ADR 0029).
   */
  const [confirmacaoLiberacoes, setConfirmacaoLiberacoes] = useState<{
    mensagem: string;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    onLoadingChange?.(loadingEsteiras);
  }, [loadingEsteiras, onLoadingChange]);

  const handleCriarEsteira = async () => {
    if (!novoEstNome.trim()) return toast.error('Nome da esteira é obrigatório.');
    setCriandoEsteira(true);
    try {
      await createEsteira({
        nome: novoEstNome.trim(),
        cor: novoEstCor,
        ordem: Number(novoEstOrdem) || 1,
        slaDiasPadrao: novoEstSla ? Number(novoEstSla) : null,
        // Esteira de sistema nunca é planejável (o backend recusa a combinação, at-84).
        planejavel: novoEstSistema ? false : novoEstPlanejavel,
        permiteColaborador: novoEstPermiteColaborador,
        sistema: novoEstSistema,
        roles: novoEstRoles,
      });
      setNovoEstNome('');
      setNovoEstCor('#ea5d0e');
      setNovoEstOrdem('1');
      setNovoEstSla('');
      setNovoEstPlanejavel(true);
      setNovoEstPermiteColaborador(false);
      setNovoEstSistema(false);
      setNovoEstRoles([...ESTEIRA_ROLES_PADRAO]);
      toast.success('Esteira criada com sucesso.');
      void queryClient.invalidateQueries({ queryKey: ['esteiras'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao criar esteira.');
    } finally {
      setCriandoEsteira(false);
    }
  };

  const handleIniciarEdicaoEsteira = (est: Esteira) => {
    setEditEstId(est.id);
    setEditEstCor(est.cor);
    setEditEstOrdem(String(est.ordem));
    setEditEstSla(est.slaDiasPadrao ? String(est.slaDiasPadrao) : '');
    setEditEstPlanejavel(est.planejavel);
    setEditEstPermiteColaborador(est.permiteColaborador);
    setEditEstContaNoTotal(est.contaNoTotal !== false);
    setEditEstSistema(est.sistema);
    setEditEstRoles(est.roles || []);
  };

  /** Extrai as liberações do 409; devolve null quando o erro é de outra natureza. */
  const liberacoesDoErro = (
    erro: any,
  ): { liberacoes: Array<{ placa: string; concedidaEm?: string }>; message?: string } | null => {
    const data = erro?.response?.data;
    if (erro?.response?.status === 409 && data?.code === 'ESTEIRA_TEM_LIBERACOES') {
      return { liberacoes: data.liberacoes ?? [], message: data.message };
    }
    return null;
  };

  const montarMensagemDeLiberacoes = (
    liberacoes: Array<{ placa: string; concedidaEm?: string }>,
    mensagemDoServidor?: string,
  ) => {
    const linhas = liberacoes
      .map((l) => {
        const quando = l.concedidaEm ? new Date(l.concedidaEm).toLocaleDateString('pt-BR') : null;
        return quando ? `- ${l.placa} (liberada em ${quando})` : `- ${l.placa}`;
      })
      .join('\n');
    const cabecalho =
      mensagemDoServidor ??
      'Esta esteira passa a ser de fluxo livre e as liberações concedidas para ela serão removidas.';
    return `${cabecalho}\n\nVeículos afetados:\n${linhas}`;
  };

  const handleSalvarEdicaoEsteira = async (confirmar = false) => {
    if (!editEstId) return;
    setSalvandoEsteira(true);
    try {
      await updateEsteira(editEstId, {
        cor: editEstCor,
        ordem: Number(editEstOrdem) || 1,
        ...(editEstSla ? { slaDiasPadrao: Number(editEstSla) } : {}),
        // Esteira de sistema nunca é planejável (o backend recusa a combinação, at-84).
        planejavel: editEstSistema ? false : editEstPlanejavel,
        permiteColaborador: editEstPermiteColaborador,
        contaNoTotal: editEstContaNoTotal,
        sistema: editEstSistema,
        roles: editEstRoles.map((r) => ({ role: r.role, tipo: r.tipo })),
        ...(confirmar ? { confirmar: true } : {}),
      });
      setEditEstId(null);
      toast.success('Esteira atualizada com sucesso.');
      void queryClient.invalidateQueries({ queryKey: ['esteiras'] });
    } catch (e: any) {
      const comLiberacoes = liberacoesDoErro(e);
      if (comLiberacoes) {
        // Nada foi gravado no servidor: a confirmação reenvia a MESMA edição com o aceite.
        setConfirmacaoLiberacoes({
          mensagem: montarMensagemDeLiberacoes(comLiberacoes.liberacoes, comLiberacoes.message),
          onConfirm: () => {
            setConfirmacaoLiberacoes(null);
            void handleSalvarEdicaoEsteira(true);
          },
        });
        return;
      }
      toast.error(e?.response?.data?.message || 'Erro ao atualizar esteira.');
    } finally {
      setSalvandoEsteira(false);
    }
  };

  const handleToggleAtivoEsteira = async (est: Esteira) => {
    try {
      await updateEsteira(est.id, { ativo: !est.ativo });
      toast.success(`Esteira "${est.nome}" ${!est.ativo ? 'ativada' : 'desativada'}.`);
      void queryClient.invalidateQueries({ queryKey: ['esteiras'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alterar status da esteira.');
    }
  };

  const handleExcluirEsteira = (est: Esteira) => {
    if (est.sistema) {
      toast.error('Esteiras do sistema não podem ser excluídas.');
      return;
    }
    setEsteiraParaExcluir(est);
  };

  const handleConfirmExcluirEsteira = async () => {
    if (!esteiraParaExcluir) return;
    const target = esteiraParaExcluir;
    setEsteiraParaExcluir(null);
    try {
      await deleteEsteira(target.id);
      toast.success('Esteira excluída.');
      void queryClient.invalidateQueries({ queryKey: ['esteiras'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao excluir esteira (esta etapa pode conter veículos ativos).');
    }
  };

  // Helper de roles
  const hasEstRole = (list: EsteiraRole[], role: string, tipo: 'SOLICITAR_APROVACAO' | 'APROVAR') =>
    list.some((r) => r.role === role && r.tipo === tipo);

  const toggleEstRole = (
    list: EsteiraRole[],
    role: string,
    tipo: 'SOLICITAR_APROVACAO' | 'APROVAR',
    setList: React.Dispatch<React.SetStateAction<EsteiraRole[]>>
  ) => {
    const exists = list.some((r) => r.role === role && r.tipo === tipo);
    if (exists) {
      setList(list.filter((r) => !(r.role === role && r.tipo === tipo)));
    } else {
      setList([...list, { role, tipo }]);
    }
  };

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Fluxos de Esteiras
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* Criar Esteira Card */}
      <div className="admin-card">
        <div className="admin-card-title">Criar Nova Esteira</div>
        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label">Nome da Esteira</label>
            <input
              type="text"
              placeholder="Ex: Alinhamento de Direção"
              value={novoEstNome}
              onChange={(e) => setNovoEstNome(e.target.value)}
              className="admin-input"
            />
          </div>

          <div className="admin-input-group fixed-width" style={{ maxWidth: '100px', minWidth: '80px' }}>
            <label className="admin-label">Cor Hex</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="color"
                value={novoEstCor}
                onChange={(e) => setNovoEstCor(e.target.value)}
                style={{ width: '32px', height: '32px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={novoEstCor}
                onChange={(e) => setNovoEstCor(e.target.value)}
                className="admin-input"
                style={{ padding: '6px', fontSize: '12px' }}
              />
            </div>
          </div>

          <div className="admin-input-group fixed-width" style={{ maxWidth: '80px', minWidth: '60px' }}>
            <label className="admin-label">Ordem</label>
            <input
              type="number"
              value={novoEstOrdem}
              onChange={(e) => setNovoEstOrdem(e.target.value)}
              className="admin-input"
            />
          </div>

          <div className="admin-input-group fixed-width" style={{ maxWidth: '100px', minWidth: '80px' }}>
            <label className="admin-label">SLA (Dias)</label>
            <input
              type="number"
              placeholder="Sem SLA"
              value={novoEstSla}
              onChange={(e) => setNovoEstSla(e.target.value)}
              className="admin-input"
            />
          </div>

          <div className="admin-input-group fixed-width">
            <label
              className="admin-checkbox-label"
              style={{ paddingBottom: '10px' }}
              title={novoEstSistema ? 'Esteira de sistema não entra no planejamento de serviços' : undefined}
            >
              <input
                type="checkbox"
                checked={novoEstPlanejavel}
                disabled={novoEstSistema}
                onChange={(e) => setNovoEstPlanejavel(e.target.checked)}
              />
              Planejável
            </label>
          </div>

          <div className="admin-input-group fixed-width">
            <label className="admin-checkbox-label" style={{ paddingBottom: '10px' }} title="Se marcado, veículos nesta esteira podem ter colaborador responsável e sessões de trabalho">
              <input
                type="checkbox"
                checked={novoEstPermiteColaborador}
                onChange={(e) => setNovoEstPermiteColaborador(e.target.checked)}
              />
              Permite colaborador
            </label>
          </div>

          <div className="admin-input-group fixed-width">
            <label className="admin-checkbox-label" style={{ paddingBottom: '10px' }} title={HINT_SISTEMA}>
              <input
                type="checkbox"
                checked={novoEstSistema}
                onChange={(e) => {
                  const marcado = e.target.checked;
                  setNovoEstSistema(marcado);
                  // As duas marcações são incompatíveis por regra — desmarcar aqui evita
                  // enviar uma combinação que o backend recusa.
                  if (marcado) setNovoEstPlanejavel(false);
                }}
              />
              Esteira de sistema
            </label>
          </div>

          <button
            onClick={handleCriarEsteira}
            disabled={criandoEsteira}
            className="admin-btn admin-btn-primary"
            style={{ height: '40px' }}
          >
            {criandoEsteira ? 'Criando...' : 'Adicionar'}
          </button>
        </div>

        <p className="admin-hint">{HINT_SISTEMA}</p>

        {/* Configurar Roles ao Criar */}
        <div style={{ marginTop: '20px', borderTop: '1px solid var(--tema-linha, #f1f3f4)', paddingTop: '16px' }}>
          <label className="admin-label">Permissões de Perfil</label>
          <p style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', marginBottom: '12px' }}>
            Selecione quais papéis cada perfil pode exercer nesta nova esteira:
          </p>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--tema-linha, #e2e8f0)', textAlign: 'left' }}>
                <th style={{ padding: '6px' }}>Perfil (Role)</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>Solicitar Entrada</th>
                <th style={{ padding: '6px', textAlign: 'center' }}>Aprovar Entrada</th>
              </tr>
            </thead>
            <tbody>
              {ALL_ROLES.map((role) => (
                <tr key={role} style={{ borderBottom: '1px solid var(--tema-linha, #f1f3f4)' }}>
                  <td style={{ padding: '6px', fontWeight: 500 }}>{role}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={hasEstRole(novoEstRoles, role, 'SOLICITAR_APROVACAO')}
                      onChange={() => toggleEstRole(novoEstRoles, role, 'SOLICITAR_APROVACAO', setNovoEstRoles)}
                    />
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={hasEstRole(novoEstRoles, role, 'APROVAR')}
                      onChange={() => toggleEstRole(novoEstRoles, role, 'APROVAR', setNovoEstRoles)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Listagem/Edição das Esteiras */}
      <div className="admin-group-title" style={{ marginTop: '24px' }}>Esteiras Cadastradas</div>
      {esteiras
        .slice()
        .sort((a, b) => a.ordem - b.ordem)
        .map((est) => {
          const isEditing = editEstId === est.id;
          return (
            <div
              key={est.id}
              className="admin-card"
              style={{
                opacity: est.ativo ? 1 : 0.6,
                borderLeft: `6px solid ${isEditing ? editEstCor : est.cor}`,
                marginBottom: '16px',
              }}
            >
              {isEditing ? (
                /* Modo Edição */
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ fontWeight: 700, fontSize: '16px' }}>
                      Editando Esteira: {est.nome}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)' }}>
                      Chave interna: {est.chave} {est.sistema && '(Sistema)'}
                    </span>
                  </div>

                  <div className="admin-form-grid">
                    <div className="admin-input-group fixed-width" style={{ maxWidth: '100px', minWidth: '80px' }}>
                      <label className="admin-label">Cor Hex</label>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={editEstCor}
                          onChange={(e) => setEditEstCor(e.target.value)}
                          style={{ width: '32px', height: '32px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          value={editEstCor}
                          onChange={(e) => setEditEstCor(e.target.value)}
                          className="admin-input"
                          style={{ padding: '6px', fontSize: '12px' }}
                        />
                      </div>
                    </div>

                    <div className="admin-input-group fixed-width" style={{ maxWidth: '80px', minWidth: '60px' }}>
                      <label className="admin-label">Ordem</label>
                      <input
                        type="number"
                        value={editEstOrdem}
                        onChange={(e) => setEditEstOrdem(e.target.value)}
                        className="admin-input"
                      />
                    </div>

                    <div className="admin-input-group fixed-width" style={{ maxWidth: '100px', minWidth: '80px' }}>
                      <label className="admin-label">SLA (Dias)</label>
                      <input
                        type="number"
                        value={editEstSla}
                        onChange={(e) => setEditEstSla(e.target.value)}
                        className="admin-input"
                      />
                    </div>

                    <div className="admin-input-group fixed-width">
                      <label
                        className="admin-checkbox-label"
                        style={{ paddingBottom: '10px' }}
                        title={editEstSistema ? 'Esteira de sistema não entra no planejamento de serviços' : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={editEstPlanejavel}
                          disabled={editEstSistema}
                          onChange={(e) => setEditEstPlanejavel(e.target.checked)}
                        />
                        Planejável
                      </label>
                    </div>

                    <div className="admin-input-group fixed-width">
                      <label className="admin-checkbox-label" style={{ paddingBottom: '10px' }} title="Se marcado, veículos nesta esteira podem ter colaborador responsável e sessões de trabalho">
                        <input
                          type="checkbox"
                          checked={editEstPermiteColaborador}
                          onChange={(e) => setEditEstPermiteColaborador(e.target.checked)}
                        />
                        Permite colaborador
                      </label>
                    </div>

                    <div className="admin-input-group fixed-width">
                      <label className="admin-checkbox-label" style={{ paddingBottom: '10px' }} title="Desmarcada, os veículos nesta esteira continuam no quadro do Painel Geral, mas não entram no card Total na oficina">
                        <input
                          type="checkbox"
                          checked={editEstContaNoTotal}
                          onChange={(e) => setEditEstContaNoTotal(e.target.checked)}
                        />
                        Conta no total na oficina
                      </label>
                    </div>

                    <div className="admin-input-group fixed-width">
                      <label
                        className="admin-checkbox-label"
                        style={{ paddingBottom: '10px' }}
                        title={
                          CHAVES_CICLO_DE_VIDA.includes(est.chave)
                            ? 'As esteiras do ciclo de vida do veículo não podem ter esta marcação alterada'
                            : HINT_SISTEMA
                        }
                      >
                        <input
                          type="checkbox"
                          checked={editEstSistema}
                          disabled={CHAVES_CICLO_DE_VIDA.includes(est.chave)}
                          onChange={(e) => {
                            const marcado = e.target.checked;
                            setEditEstSistema(marcado);
                            if (marcado) setEditEstPlanejavel(false);
                          }}
                        />
                        Esteira de sistema
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        // ⚠️ Arrow obrigatória: passar o handler direto entregaria o MouseEvent
                        // como `confirmar` — e evento é truthy, o que aceitaria a remoção das
                        // liberações sem nunca mostrar a confirmação ao ADMIN.
                        onClick={() => void handleSalvarEdicaoEsteira()}
                        disabled={salvandoEsteira}
                        className="admin-btn admin-btn-primary"
                      >
                        {salvandoEsteira ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        onClick={() => setEditEstId(null)}
                        className="admin-btn admin-btn-secondary"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>

                  <p className="admin-hint">
                    {CHAVES_CICLO_DE_VIDA.includes(est.chave)
                      ? 'Esta etapa faz parte do ciclo de vida do veículo: a marcação de sistema dela não pode ser alterada.'
                      : HINT_SISTEMA}
                  </p>

                  {/* Permissões na Edição */}
                  <div style={{ marginTop: '20px', borderTop: '1px solid var(--tema-linha, #f1f3f4)', paddingTop: '16px' }}>
                    <label className="admin-label">Permissões de Perfil</label>
                    <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '8px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--tema-linha, #e2e8f0)', textAlign: 'left' }}>
                          <th style={{ padding: '6px' }}>Perfil (Role)</th>
                          <th style={{ padding: '6px', textAlign: 'center' }}>Solicitar Entrada</th>
                          <th style={{ padding: '6px', textAlign: 'center' }}>Aprovar Entrada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ALL_ROLES.map((role) => (
                          <tr key={role} style={{ borderBottom: '1px solid var(--tema-linha, #f1f3f4)' }}>
                            <td style={{ padding: '6px', fontWeight: 500 }}>{role}</td>
                            <td style={{ padding: '6px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={hasEstRole(editEstRoles, role, 'SOLICITAR_APROVACAO')}
                                onChange={() => toggleEstRole(editEstRoles, role, 'SOLICITAR_APROVACAO', setEditEstRoles)}
                              />
                            </td>
                            <td style={{ padding: '6px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={hasEstRole(editEstRoles, role, 'APROVAR')}
                                onChange={() => toggleEstRole(editEstRoles, role, 'APROVAR', setEditEstRoles)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* Modo Visualização */
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 700, fontSize: '17px', color: 'var(--tema-texto, #1A1C1B)' }}>
                        {est.nome}
                      </span>
                      <span
                        style={{
                          backgroundColor: est.cor,
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          display: 'inline-block',
                        }}
                        title={est.cor}
                      />
                      {!est.ativo && <span className="admin-badge-inactive">INATIVA</span>}
                      {est.sistema && <span className="admin-badge-inactive" style={{ backgroundColor: 'var(--tema-tinta-azul, #e0f2fe)', color: 'var(--tema-texto-azul, #0369a1)' }}>SISTEMA</span>}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--tema-texto-2, #5E5E5E)' }}>
                      Chave: <code style={{ background: 'var(--tema-fundo, #f1f3f4)', padding: '2px 4px', borderRadius: '4px' }}>{est.chave}</code> | Ordem: <strong>{est.ordem}</strong> | SLA: <strong>{est.slaDiasPadrao ? `${est.slaDiasPadrao} dias` : 'Sem limite'}</strong> | Planejável: <strong>{est.planejavel ? 'Sim' : 'Não'}</strong> | Colaborador: <strong>{est.permiteColaborador ? 'Sim' : 'Não'}</strong> | Conta no total: <strong>{est.contaNoTotal === false ? 'Não' : 'Sim'}</strong>
                    </div>

                    {/* Mostrar permissões ativas de forma resumida */}
                    <div style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', marginTop: '6px' }}>
                      <strong>Pode Solicitar:</strong> {est.roles?.filter((r) => r.tipo === 'SOLICITAR_APROVACAO').map((r) => r.role).join(', ') || 'Nenhum'} <br />
                      <strong>Pode Aprovar:</strong> {est.roles?.filter((r) => r.tipo === 'APROVAR').map((r) => r.role).join(', ') || 'Nenhum'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleIniciarEdicaoEsteira(est)}
                      className="admin-btn admin-btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleToggleAtivoEsteira(est)}
                      className="admin-btn admin-btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                      {est.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                    {!est.sistema && (
                      <button
                        onClick={() => handleExcluirEsteira(est)}
                        className="admin-btn admin-btn-danger-outline"
                        style={{ padding: '6px 14px', fontSize: '12px' }}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {esteiraParaExcluir && (
        <ConfirmModal
          isOpen={esteiraParaExcluir !== null}
          message={`Deseja realmente remover a esteira "${esteiraParaExcluir.nome}"? Esta ação não pode ser desfeita.`}
          confirmLabel="Remover"
          onConfirm={handleConfirmExcluirEsteira}
          onCancel={() => setEsteiraParaExcluir(null)}
        />
      )}

      {confirmacaoLiberacoes && (
        <ConfirmModal
          isOpen
          message={confirmacaoLiberacoes.mensagem}
          confirmLabel="Marcar e remover liberações"
          onConfirm={() => confirmacaoLiberacoes.onConfirm()}
          onCancel={() => setConfirmacaoLiberacoes(null)}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Fluxos de Esteiras"
        secoes={helpSections}
      />
    </>
  );
};

export default FluxoEsteirasSection;
